import type { Contact } from '@crm/entities/contact';
import type {
  AiAccessVerdict,
  OrganizationSettings,
} from '@travel-planner/shared/schemas';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { aiAccessVerdict } from '@travel-planner/shared/schemas';
import { FileText, Globe, Info, MapPin, Plane, User } from 'lucide-react';
import { formatUtcDate } from '@/shared/lib/format-utc-date';
import { Heading, Skeleton, Text } from '@/shared/ui';
import { isContactFieldVisible } from '../model/toggleable-fields';
import {
  consortium,
  contactSalutation,
  lifecycleStage,
  preferredHotelType,
  specialOccasion,
} from '../model/types';
import css from './contact-detail-view.module.scss';

const AI_ASSISTANT_LABEL: Record<AiAccessVerdict, string> = {
  allowed: 'Allowed',
  blocked: 'Blocked',
  not_registered: 'Not available',
};

/** XT-903 — the rule the MCP door applies (`aiAccessVerdict`), on the contact shown. */
export const aiAssistantStatus = (
  contact: Pick<Contact, 'aiBlocked'>
): string =>
  AI_ASSISTANT_LABEL[
    aiAccessVerdict({ kind: 'contact', aiBlocked: contact.aiBlocked })
  ];

type ContactDetailViewProps = {
  contact?: Contact | null;
  isLoading: boolean;
  /**
   * Org settings that drive per-field visibility. When omitted (or a field key
   * is absent) every toggleable field is shown — see `isContactFieldVisible`.
   */
  settings?: OrganizationSettings | null;
};

type FieldSpec = {
  label: string;
  value: ReactNode;
  /**
   * When set, this field is org-toggleable — it renders only when the org has
   * not hidden the key. Core fields omit this and always render.
   */
  fieldKey?: string;
};

const isEmpty = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const enumLabel = <T extends string>(
  map: Record<string, T>,
  value: string | null | undefined
): string | null => {
  if (!value) return null;
  const entry = Object.entries(map).find(([, label]) => label === value);
  return entry?.[1] ?? value;
};

const formatDate = (iso: string | null | undefined): string | null =>
  iso ? formatUtcDate(iso, 'MMMM d, yyyy') : null;

const formatDateTime = (iso: string | null | undefined): string | null =>
  iso ? formatUtcDate(iso, 'MMM d, yyyy HH:mm') : null;

const ChipList = ({ items }: { items: string[] | null | undefined }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={css.chipList}>
      {items.map((item) => (
        <span className={css.chip} key={item}>
          {item}
        </span>
      ))}
    </div>
  );
};

const Field = ({
  label,
  value,
  isLoading,
}: {
  label: string;
  value?: ReactNode;
  isLoading: boolean;
}) => {
  if (!isLoading && isEmpty(value)) return null;
  return (
    <div className={css.field}>
      <Text size="sm" variant="muted">
        {label}
      </Text>
      {isLoading ? (
        <Skeleton height={16} width="120px" />
      ) : (
        <Text>{value}</Text>
      )}
    </div>
  );
};

const Section = ({
  title,
  icon: Icon,
  children,
  isLoading,
  hasContent,
}: {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: ReactNode;
  isLoading: boolean;
  hasContent: boolean;
}) => {
  if (!isLoading && !hasContent) return null;
  return (
    <section className={css.section}>
      <Heading as="h2" className={css.sectionTitle} size="md">
        <Icon className={css.sectionIcon} size={18} />
        {title}
      </Heading>
      {children}
    </section>
  );
};

const renderFieldGrid = (
  fields: FieldSpec[],
  isLoading: boolean,
  settings: OrganizationSettings | null | undefined
): ReactNode => {
  // Drop org-hidden toggleable fields first, then (when not loading) empty ones.
  const orgVisible = fields.filter(
    (f) => !f.fieldKey || isContactFieldVisible(f.fieldKey, settings)
  );
  const visibleFields = isLoading
    ? orgVisible
    : orgVisible.filter((f) => !isEmpty(f.value));
  if (!isLoading && visibleFields.length === 0) return null;
  return (
    <div className={css.fields}>
      {visibleFields.map(({ label, value }) => (
        <Field key={label} isLoading={isLoading} label={label} value={value} />
      ))}
    </div>
  );
};

const getAddressLine = (contact: Contact | null | undefined): string => {
  if (!contact) return '';
  const raw = [
    contact.streetAddress,
    contact.city,
    contact.stateOrRegion,
    contact.postalCode,
    contact.country,
  ]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter((v) => /\w/u.test(v))
    .join(', ');

  return raw
    .replace(/,\s*,+/g, ',')
    .replace(/^,\s*/, '')
    .replace(/,\s*$/, '')
    .trim();
};

const AddressBlock = ({ contact }: { contact?: Contact | null }) => {
  const line = getAddressLine(contact);
  if (!line) return null;
  return <Text className={css.addressBlock}>{line}</Text>;
};

const NotesBlock = ({ children }: { children: ReactNode }) => {
  if (isEmpty(children)) return null;
  return <div className={css.notesBlock}>{children}</div>;
};

export const ContactDetailView = ({
  contact,
  isLoading,
  settings,
}: ContactDetailViewProps) => {
  const companyNode =
    contact?.company?.name && contact.companyId ? (
      <Link
        className={css.link}
        params={{ companyId: contact.companyId }}
        to="/crm/accounts/$companyId"
      >
        {contact.company.name}
      </Link>
    ) : contact?.company?.name ? (
      <Text>{contact.company.name}</Text>
    ) : null;

  const contactFields: FieldSpec[] = [
    {
      label: 'Salutation',
      value: enumLabel(contactSalutation, contact?.salutation),
    },
    { label: 'Title', value: contact?.title },
    { label: 'Email', value: contact?.email },
    { label: 'Office phone', value: contact?.officePhone },
    { label: 'Mobile phone', value: contact?.mobilePhone },
    { label: 'WhatsApp', value: contact?.whatsApp },
    { label: 'Company', value: companyNode },
    {
      label: 'Lifecycle',
      value: enumLabel(lifecycleStage, contact?.lifecycleStage),
      fieldKey: 'lifecycleStage',
    },
    {
      label: 'Consortium',
      value: enumLabel(consortium, contact?.consortium),
      fieldKey: 'consortium',
    },
    { label: 'Agent status', value: contact?.agentStatus },
    // XT-903 — may this agent's AI assistant connect to Eleggia.
    {
      label: 'AI assistant',
      value: contact ? aiAssistantStatus(contact) : undefined,
    },
    {
      label: 'Labels',
      value:
        contact?.label && contact.label.length > 0 ? (
          <ChipList items={contact.label} />
        ) : null,
    },
  ];

  const addressLine = getAddressLine(contact);

  const onlineFields: FieldSpec[] = [
    { label: 'WeChat', value: contact?.weChatId },
    { label: 'Skype', value: contact?.skypeId },
    { label: 'LinkedIn', value: contact?.linkedin },
    { label: 'Facebook', value: contact?.facebook },
    { label: 'Instagram', value: contact?.instagram },
  ];

  const travelPreferencesFields: FieldSpec[] = [
    {
      label: 'Preferred hotel type',
      value: enumLabel(preferredHotelType, contact?.preferredHotelType),
      fieldKey: 'preferredHotelType',
    },
    {
      label: 'Regions of interest',
      value: contact?.regionsOfInterest?.join(', '),
      fieldKey: 'regionsOfInterest',
    },
    {
      label: 'Estimated budget',
      value: contact?.estimatedBudget,
      fieldKey: 'estimatedBudget',
    },
    { label: 'Days', value: contact?.numberOfDays, fieldKey: 'numberOfDays' },
    {
      label: 'Passengers',
      value: contact?.numberOfPassengers,
      fieldKey: 'numberOfPassengers',
    },
    {
      label: 'Arrival',
      value: formatDate(contact?.arrivalDate),
      fieldKey: 'arrivalDate',
    },
    {
      label: 'Departure',
      value: formatDate(contact?.departureDate),
      fieldKey: 'departureDate',
    },
    {
      label: 'Food restrictions',
      value: contact?.foodRestrictions,
      fieldKey: 'foodRestrictions',
    },
    {
      label: 'Special occasion',
      value: enumLabel(specialOccasion, contact?.specialOccasion),
      fieldKey: 'specialOccasion',
    },
    {
      label: 'Traveller interests',
      value: contact?.travellerInterests?.join(', '),
      fieldKey: 'travellerInterests',
    },
  ];

  const additionalFields: FieldSpec[] = [
    { label: 'Industry', value: contact?.industry },
    {
      label: 'Lead source',
      value:
        contact?.leadSource && contact.leadSource.length > 0 ? (
          <ChipList items={contact.leadSource} />
        ) : null,
    },
    { label: 'Preferred language', value: contact?.preferredLanguage },
    { label: 'Contact origin', value: contact?.contactOrigin },
    { label: 'External ID', value: contact?.externalId },
    { label: 'Created on', value: formatDateTime(contact?.createdOn) },
    { label: 'Modified on', value: formatDateTime(contact?.modifiedOn) },
  ];

  // A toggleable field only counts toward "has content" when the org shows it,
  // so a section whose visible fields are all org-hidden disappears entirely.
  const hasVisibleContent = (fields: FieldSpec[]) =>
    fields.some(
      (f) =>
        (!f.fieldKey || isContactFieldVisible(f.fieldKey, settings)) &&
        !isEmpty(f.value)
    );

  const hasContactContent = isLoading || hasVisibleContent(contactFields);
  const hasAddressContent = isLoading || addressLine.length > 0;
  const hasOnlineContent = isLoading || hasVisibleContent(onlineFields);
  const hasTravelContent =
    isLoading || hasVisibleContent(travelPreferencesFields);
  const hasNotesContent =
    isLoading ||
    (isContactFieldVisible('tripBriefing', settings) &&
      !isEmpty(contact?.tripBriefing)) ||
    !isEmpty(contact?.notes);
  const hasAdditionalContent = isLoading || hasVisibleContent(additionalFields);

  return (
    <div className={css.root}>
      <Section
        hasContent={hasContactContent}
        icon={User}
        isLoading={isLoading}
        title="Contact information"
      >
        {renderFieldGrid(contactFields, isLoading, settings)}
      </Section>

      <Section
        hasContent={hasAddressContent}
        icon={MapPin}
        isLoading={isLoading}
        title="Address"
      >
        {isLoading ? (
          <Field isLoading label="Address" value="" />
        ) : (
          <div className={css.field}>
            <Text size="sm" variant="muted">
              Address
            </Text>
            <AddressBlock contact={contact} />
          </div>
        )}
      </Section>

      <Section
        hasContent={hasOnlineContent}
        icon={Globe}
        isLoading={isLoading}
        title="Online presence"
      >
        {renderFieldGrid(onlineFields, isLoading, settings)}
      </Section>

      <Section
        hasContent={hasTravelContent}
        icon={Plane}
        isLoading={isLoading}
        title="Travel preferences"
      >
        {renderFieldGrid(travelPreferencesFields, isLoading, settings)}
      </Section>

      <Section
        hasContent={hasNotesContent}
        icon={FileText}
        isLoading={isLoading}
        title="Notes"
      >
        <div className={css.fields}>
          {isLoading ? (
            <>
              <Field isLoading label="Notes" value="" />
              <Field isLoading label="Trip briefing" value="" />
            </>
          ) : (
            <>
              {!isEmpty(contact?.notes) && (
                <div className={css.field}>
                  <Text size="sm" variant="muted">
                    Notes
                  </Text>
                  <NotesBlock>{contact?.notes}</NotesBlock>
                </div>
              )}
              {isContactFieldVisible('tripBriefing', settings) &&
                !isEmpty(contact?.tripBriefing) && (
                  <div className={css.field}>
                    <Text size="sm" variant="muted">
                      Trip briefing
                    </Text>
                    <NotesBlock>{contact?.tripBriefing}</NotesBlock>
                  </div>
                )}
            </>
          )}
        </div>
      </Section>

      <Section
        hasContent={hasAdditionalContent}
        icon={Info}
        isLoading={isLoading}
        title="Additional information"
      >
        {renderFieldGrid(additionalFields, isLoading, settings)}
      </Section>
    </div>
  );
};
